import React, { useEffect, useState } from 'react';
import { actorInfo } from '../mock/komalData';
import { ChevronDown, Play, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';

const KomalHero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative h-screen w-full overflow-hidden bg-black">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <OptimizedImage
          src={actorInfo.photos.hero}
          alt="Komal Verma"
          className="h-full w-full object-cover"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
          <div className={`max-w-3xl transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Name */}
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white mb-3 sm:mb-4 tracking-tight leading-none">
              KOMAL
              <br />
              <span className="text-[#FF6B6B]">VERMA</span>
            </h1>
            
            {/* Tagline */}
            <p className="text-lg sm:text-2xl md:text-3xl text-gray-200 font-light mb-2 sm:mb-4 tracking-wide">
              {actorInfo.tagline}
            </p>
            
            {/* Additional Tagline */}
            <p className="text-base sm:text-xl md:text-2xl text-[#FF6B6B] font-medium italic mb-4 sm:mb-8 tracking-wide">
              I act with my heart. You'll feel it in yours.
            </p>
            
            {/* Short Bio */}
            <p className="text-sm sm:text-lg text-gray-300 mb-6 sm:mb-10 leading-relaxed max-w-2xl">
              {actorInfo.shortBio}
            </p>

            {/* CTA Buttons - Two rows by priority */}
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <button
                  onClick={() => scrollToSection('showreel')}
                  className="group px-6 py-3 sm:px-8 sm:py-4 bg-[#FF6B6B] text-white font-bold text-sm sm:text-base rounded-sm hover:bg-[#FF8E53] transition-all duration-300 flex items-center gap-2 sm:gap-3 hover:scale-105 hover:shadow-xl shadow-[#FF6B6B]/50"
                >
                  <Play size={18} className="group-hover:scale-110 transition-transform" />
                  Watch Showreel
                </button>
                <Link
                  to="/portfolio"
                  className="group px-6 py-3 sm:px-8 sm:py-4 bg-[#4ECDC4] text-white font-bold text-sm sm:text-base rounded-sm hover:bg-[#3AB8AD] transition-all duration-300 flex items-center gap-2 sm:gap-3 hover:scale-105 hover:shadow-xl shadow-[#4ECDC4]/50"
                  data-testid="hero-portfolio-link"
                >
                  <Camera size={18} className="group-hover:scale-110 transition-transform" />
                  Portfolio
                </Link>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <button
                  onClick={() => scrollToSection('work')}
                  className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-white text-white font-bold text-sm sm:text-base rounded-sm hover:bg-white hover:text-black transition-all duration-300"
                >
                  Explore My Work
                </button>
                <button
                  onClick={() => scrollToSection('portraits')}
                  className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-[#FFD700] text-[#FFD700] font-bold text-sm sm:text-base rounded-sm hover:bg-[#FFD700] hover:text-black transition-all duration-300"
                >
                  View Portraits
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <button
          onClick={() => scrollToSection('showreel')}
          className="flex flex-col items-center gap-2 text-white hover:text-[#FF6B6B] transition-colors group"
          aria-label="Scroll down"
        >
          <span className="text-sm font-medium tracking-wider uppercase">Scroll</span>
          <ChevronDown size={32} className="animate-bounce group-hover:text-[#FF6B6B]" />
        </button>
      </div>

      {/* Quick Navigation Dots */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-50 hidden lg:flex flex-col gap-4">
        {['home', 'about', 'showreel', 'portraits', 'skills', 'shades', 'work', 'contact'].map((section, index) => (
          <button
            key={section}
            onClick={() => scrollToSection(section)}
            className="w-3 h-3 rounded-full bg-white/30 hover:bg-[#FF6B6B] transition-all duration-300 hover:scale-150"
            aria-label={`Go to ${section}`}
            title={section.charAt(0).toUpperCase() + section.slice(1)}
          />
        ))}
      </div>
    </section>
  );
};

export default KomalHero;
